import { useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { toast } from "sonner";
import { Boxes, ArrowRight, AlertTriangle, Pencil } from "lucide-react";
import EditarDesgloseDialog from "@/components/EditarDesgloseDialog";

/** Cuadro "Último desglose ×N" en la ficha: antes → después de cada valor. */
export default function DesgloseResumenCard({ producto, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const hist = producto?.desglose_historial || [];
  if (!hist.length) return null;
  const d = hist[hist.length - 1];
  const factorVigente = d.factor;
  const deshecho = d.tipo === "editar" && d.factor === 1;
  const fecha = d.at ? new Date(d.at).toLocaleString("es-CO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  const pendienteReinterpretar = hist.some((h) => h.ventas_previas === "paquete" && !h.tipo) && !hist.some((h) => h.tipo === "reinterpretar_ventas");

  const reinterpretar = async () => {
    if (!window.confirm("Esto asume que los pedidos anteriores al desglose ya iban en SUB-PAQUETE (no en paquetes completos): corrige el costo congelado de esos pedidos (÷N), el consumo de los lotes y recupera el stock disponible. ¿Continuar?")) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/productos/${producto.id}/desglosar/reinterpretar-ventas`);
      toast.success(`Corregido: ${data.pedidos_snapshot} pedido(s), ${data.despachos} despacho(s) · stock ${formatNumber(data.stock_viejo)} → ${formatNumber(data.stock_nuevo)}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo corregir"); }
    finally { setSaving(false); }
  };

  return (
    <div className="col-span-2 rounded-xl border border-violet-200 bg-violet-50/60 dark:bg-violet-900/15 dark:border-violet-800 p-3" data-testid="desglose-resumen-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-bold uppercase tracking-wide text-violet-800 dark:text-violet-200 flex items-center gap-1.5">
          <Boxes className="w-3.5 h-3.5" /> {d.tipo === "reinterpretar_ventas" ? "Corrección de ventas previas" : d.tipo === "editar" ? (deshecho ? "Desglose deshecho" : `Desglose corregido ×${d.factor_anterior} → ×${d.factor}`) : `Último desglose ×${d.factor}`}
        </div>
        <div className="flex items-center gap-2">
          {!deshecho && (
            <button type="button" onClick={() => setEditOpen(true)} className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1" data-testid="desglose-editar-btn">
              <Pencil className="w-3 h-3" /> Editar desglose / costo
            </button>
          )}
          <div className="text-[11px] text-muted-foreground">{fecha}{d.por ? ` · ${d.por}` : ""}{hist.length > 1 ? ` · ${hist.length} cambios` : ""}</div>
        </div>
      </div>
      <EditarDesgloseDialog open={editOpen} onClose={() => setEditOpen(false)} producto={producto} factorActual={factorVigente} onDone={() => onChanged?.()} />
      {d.motivo && <div className="text-[11px] text-muted-foreground mt-1 italic">"{d.motivo}"</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 text-xs">
        <Cambio label="Stock" a={formatNumber(d.stock_viejo)} b={formatNumber(d.stock_nuevo)} testid="desglose-cambio-stock" />
        {d.costo_nuevo != null && <Cambio label="Costo" a={formatCOP(d.costo_viejo || 0)} b={formatCOP(d.costo_nuevo)} />}
        {d.precio_minimo_nuevo != null && <Cambio label="Precio mínimo" a={formatCOP(d.precio_minimo_viejo || 0)} b={formatCOP(d.precio_minimo_nuevo)} testid="desglose-cambio-min" />}
        {d.precio_sugerido_nuevo != null && <Cambio label="Precio sugerido" a={formatCOP(d.precio_sugerido_viejo || 0)} b={formatCOP(d.precio_sugerido_nuevo)} testid="desglose-cambio-sug" />}
        {d.unidades_por_caja_nuevo != null && <Cambio label="Und. por caja" a={formatNumber(d.unidades_por_caja_viejo || 0)} b={formatNumber(d.unidades_por_caja_nuevo)} />}
        {d.costo_manual != null && <div className="text-[11px] text-muted-foreground">Costo por unidad fijado manualmente: <b>{formatCOP(d.costo_manual)}</b></div>}
        <div className="rounded-md border border-border bg-card p-2">
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Alcance</div>
          <div className="tabular-nums">{d.lotes} lote(s) · {d.despachos} despacho(s){d.pedidos_snapshot ? ` · ${d.pedidos_snapshot} pedido(s) recosteados` : ""}</div>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground mt-2">
        Ventas anteriores tratadas como: <b>{d.ventas_previas === "paquete" ? "paquetes completos" : "sub-paquetes (unidad nueva)"}</b>.
      </div>
      {pendienteReinterpretar && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-2 text-[11px] text-amber-800 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            Este desglose asumió que antes vendías <b>paquetes completos</b>. Si en realidad ya vendías por sub-paquete, el costo de esos pedidos quedó ×{d.factor} inflado en Finanzas.
            <button type="button" onClick={reinterpretar} disabled={saving} className="ml-1 font-bold text-primary hover:underline" data-testid="desglose-reinterpretar-btn">
              {saving ? "Corrigiendo..." : "Las ventas anteriores eran en sub-paquete → corregir"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Cambio({ label, a, b, testid }) {
  return (
    <div className="rounded-md border border-border bg-card p-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="flex items-center gap-1 tabular-nums"><span className="text-muted-foreground line-through">{a}</span><ArrowRight className="w-3 h-3 text-primary" /><b>{b}</b></div>
    </div>
  );
}
